import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Form, Pagination, InputGroup, Modal } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import axios from 'axios';
import { BsPencilSquare, BsTrash, BsSearch, BsSortDown, BsSortUp } from 'react-icons/bs';
import { API_ENDPOINTS } from '../config';  // Updated to use the config file

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [subscriptionsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('subscriptionId');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [formData, setFormData] = useState({
    userId: '',
    name: '',
    transactionId: '',
    planId: '',
    months: '',
    price: '',
    status: ''
  });

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.getAllSubscriptionDetails, {
        headers: {
          'token': localStorage.getItem('token'),
        }
      });
      setSubscriptions(response.data.subscriptions);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  };

  const sortSubscriptions = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAndFilteredSubscriptions = subscriptions
    .filter(subscription =>
      subscription.subscriptionId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscription.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (a[sortField] < b[sortField]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortField] > b[sortField]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const indexOfLastSubscription = currentPage * subscriptionsPerPage;
  const indexOfFirstSubscription = indexOfLastSubscription - subscriptionsPerPage;
  const currentSubscriptions = sortedAndFilteredSubscriptions.slice(indexOfFirstSubscription, indexOfLastSubscription);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleEdit = (subscription) => {
    setSelectedSubscription(subscription);
    setFormData({
      userId: subscription.userId,
      name: subscription.name,
      transactionId: subscription.transactionId,
      planId: subscription.planId,
      months: subscription.months,
      price: subscription.price,
      status: subscription.status,
    });
    setShowEditModal(true);
  };

  const handleSaveChanges = async () => {
    try {
      const response = await axios.patch(API_ENDPOINTS.editSubscription, formData, {
        headers: {
          'token': localStorage.getItem('token'),
          'subscriptionid': selectedSubscription.subscriptionId
        }
      });

      if (response.data.status) {
        console.log('Subscription updated successfully:', response.data.subscription);
        fetchSubscriptions(); // Refresh the list after editing
        setShowEditModal(false); // Close the modal
      } else {
        console.error('Failed to update subscription:', response.data.error);
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
    }
  };

  const handleDelete = async (subscriptionId) => {
    try {
      await axios.delete(API_ENDPOINTS.deleteSubscription, {
        headers: {
          'token': localStorage.getItem('token'),
          'subscriptionid': subscriptionId
        }
      });
      setSubscriptions(subscriptions.filter(sub => sub.subscriptionId !== subscriptionId));
    } catch (error) {
      console.error('Error deleting subscription:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  return (
    <div className="d-flex">
      <Sidebar style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: '250px', zIndex: 1000 }} />
      <div style={{ width: 'calc(100%)', height: 'calc(100vh)', overflowY: 'auto' }}>
        <Header style={{ position: 'fixed', top: 0, left: '250px', width: 'calc(100% - 250px)', zIndex: 1000 }} />
        <Container fluid className="mt-3">
          <Row>
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Subscriptions</h2>
              </div>
              <InputGroup className="mb-3">
                <InputGroup.Text><BsSearch /></InputGroup.Text>
                <Form.Control
                  placeholder="Search subscriptions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th onClick={() => sortSubscriptions('subscriptionId')}>
                      Subscription ID {sortField === 'subscriptionId' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortSubscriptions('name')}>
                      Name {sortField === 'name' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortSubscriptions('months')}>
                      Months {sortField === 'months' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortSubscriptions('price')}>
                      Price {sortField === 'price' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortSubscriptions('startDate')}>
                      Start Date {sortField === 'startDate' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortSubscriptions('endDate')}>
                      End Date {sortField === 'endDate' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortSubscriptions('status')}>
                      Status {sortField === 'status' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentSubscriptions.map((subscription) => (
                    <tr key={subscription.subscriptionId}>
                      <td>{subscription.subscriptionId}</td>
                      <td>{subscription.name}</td>
                      <td>{subscription.months}</td>
                      <td>${subscription.price ? subscription.price.toFixed(2) : 'N/A'}</td>
                      <td>{new Date(subscription.startDate).toISOString().split('T')[0]}</td>
                      <td>{new Date(subscription.endDate).toISOString().split('T')[0]}</td>
                      <td>{subscription.status}</td>
                      <td className="d-flex align-items-center">
                        <Button variant="info" className="me-2" onClick={() => handleEdit(subscription)}>
                          <BsPencilSquare />
                        </Button>
                        <Button variant="danger" onClick={() => handleDelete(subscription.subscriptionId)}>
                          <BsTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination>
                {[...Array(Math.ceil(sortedAndFilteredSubscriptions.length / subscriptionsPerPage)).keys()].map(number => (
                  <Pagination.Item key={number + 1} active={number + 1 === currentPage} onClick={() => paginate(number + 1)}>
                    {number + 1}
                  </Pagination.Item>
                ))}
              </Pagination>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Edit Subscription Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formMonths">
              <Form.Label>Months</Form.Label>
              <Form.Control
                type="number"
                name="months"
                value={formData.months}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveChanges}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Subscriptions;
